import { Copy, Share2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { AccountReferralRow } from "@/types/account.ui";
import {
  DataTable,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import type { ReferralLinkResponseDto } from "@/types/referral.dto";

export const referralColumns: DataTableColumn<AccountReferralRow>[] = [
  {
    id: "name",
    header: "Name",
    accessorKey: "name",
    sortable: true,
    mobilePriority: 1,
    cell: (r) => <span className="font-semibold">{r.name}</span>,
  },
  {
    id: "joinedDate",
    header: "Joined",
    accessorKey: "joinedDate",
    sortable: true,
    mobileLabel: "Joined",
    mobilePriority: 2,
    className: "text-sm text-muted-foreground",
  },
  {
    id: "totalOrders",
    header: "Orders",
    accessorKey: "totalOrders",
    sortable: true,
    align: "right",
    mobileLabel: "Orders",
    mobilePriority: 3,
  },
  {
    id: "commissionEarned",
    header: "Commission Earned",
    accessorKey: "commissionEarned",
    sortable: true,
    align: "right",
    mobileLabel: "Commission",
    mobilePriority: 4,
    cell: (r) => <span className="font-bold text-success">₹{r.commissionEarned}</span>,
  },
];

export const renderReferralCard = (r: AccountReferralRow) => (
  <div className="flex items-center justify-between rounded-lg border border-border p-3">
    <div>
      <p className="text-sm font-semibold">{r.name}</p>
      <p className="text-xs text-muted-foreground">
        Joined {r.joinedDate} • Level {r.level}
      </p>
    </div>
    <span className="text-sm font-bold text-success">₹{r.commissionEarned}</span>
  </div>
);

export function ReferralTeamTable({
  title,
  data,
  viewMode = "table",
  emptyMessage = "No team members found.",
}: {
  title: string;
  data: AccountReferralRow[];
  viewMode?: "table" | "cards";
  emptyMessage?: string;
}) {
  const table = useDataTable<AccountReferralRow>({
    data,
    searchKeys: ["name"],
  });

  return (
    <DataTable
      title={`${title} (${data.length})`}
      columns={referralColumns}
      data={table.rows}
      getRowId={(row) => row.id}
      search={table.state.search}
      onSearchChange={table.setSearch}
      searchPlaceholder="Search by name..."
      sort={table.state.sort}
      onSortChange={table.toggleSort}
      pagination={table.pagination}
      onPageChange={table.setPage}
      onPageSizeChange={table.setPageSize}
      renderMobileCard={renderReferralCard}
      viewMode={viewMode}
      emptyMessage={emptyMessage}
    />
  );
}

export function ReferralCodeCard({
  referralLink,
  referralCode,
}: {
  referralLink?: ReferralLinkResponseDto;
  referralCode: string;
}) {
  const { toast } = useToast();

  return (
    <Card className="border-accent/20 bg-gradient-warm">
      <CardContent className="p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold">Your Referral Code</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Share this code to grow your direct team.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="rounded-lg border border-border bg-card px-4 py-2 font-heading text-lg font-bold tracking-widest text-primary">
                {referralCode}
              </code>
              <Button
                size="icon"
                variant="outline"
                className="h-10 w-10"
                onClick={() => {
                  navigator.clipboard.writeText(referralCode);
                  toast({ title: "Copied!" });
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Button
            className="gap-2 self-start"
            onClick={() => {
              const url =
                referralLink?.referralUrl ??
                `${window.location.origin}/auth/register?ref=${referralCode}`;
              if (navigator.share) {
                navigator.share({
                  title: "Join Bihari Mall",
                  text: `Use my referral code ${referralCode}`,
                  url,
                });
              } else {
                navigator.clipboard.writeText(url);
                toast({ title: "Referral link copied!" });
              }
            }}
          >
            <Share2 className="h-4 w-4" /> Share Link
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
